<template>
  <div class="payment-card-container tw-col-span-3">
    <div v-if="paymentError" class="alert alert-danger" role="alert">
      {{ paymentError }}
      <b-button
        v-if="!needsSupport"
        variant="link"
        :disabled="isSavingDefault"
        @click="retryDefaultChange"
      >
        {{ failedCardId ? "Retry" : "Reload payment methods" }}
      </b-button>
    </div>
    <p v-if="defaultSaved" role="status">Default card updated.</p>
    <div v-if="meshulamError" class="alert alert-danger" role="alert">{{ meshulamError }}</div>
    <p v-if="meshulamStatus" role="status">{{ meshulamStatus }}</p>
    <div class="scroll-box">
      <div class="card-desk">
        <payment-card v-for="card in cards" :key="card.id">
          <template #body>
            <div class="card-body">
              <h3 class="card-title">{{ card.attributes.brand }}</h3>
              <div class="d-flex align-items-center">
                <i>
                  <img
                    v-if="card.attributes.type !== 'card'"
                    src="https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/donor_dashboard/bank-place.png"
                  />
                  <img
                    v-else
                    src="https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/donor_dashboard/bank-card.png"
                  />
                </i>
                <div class="card-text">
                  <p>Ending in {{ card.attributes.last4 }}</p>
                  <p v-if="!card.isBankAccount">
                    Expires {{ card.attributes.expires_month + "/" + card.attributes.expires_year }}
                  </p>
                  <p v-else>Status: expired</p>
                  <p v-if="card.attributes.default">Default</p>
                </div>
              </div>
            </div>
          </template>
          <template #footer>
            <div class="card-footer">
              <b-button
                v-if="card.attributes.type === 'card' && !card.attributes.default"
                variant="link"
                class="set-default-card"
                :disabled="isSavingDefault"
                :aria-label="`Set card ending in ${card.attributes.last4} as default`"
                @click="setDefaultCard(card.id)"
              >
                {{ savingCardId === card.id ? "Saving…" : "Set as default" }}
              </b-button>
              <b-button
                @click="showDeletePaymentModal(card)"
                ref="btnShow"
                v-b-modal.delete-payment-method
                class="btn-del"
                :disabled="isSavingDefault"
              >
                <i></i>
                <span>Delete</span>
              </b-button>
            </div>
          </template>
        </payment-card>
        <payment-card v-for="card in meshulamCards" :key="`meshulam-${card.id}`">
          <template #body>
            <div class="card-body">
              <h3 class="card-title">{{ card.brand || "Card" }}</h3>
              <div class="d-flex align-items-center">
                <i>
                  <img
                    src="https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/donor_dashboard/bank-card.png"
                  />
                </i>
                <div class="card-text">
                  <p>Ending in {{ card.last4 }}</p>
                  <p v-if="card.exp_month && card.exp_year">
                    Expires {{ card.exp_month + "/" + card.exp_year }}
                  </p>
                  <p>Meshulam</p>
                </div>
              </div>
            </div>
          </template>
          <template #footer>
            <div class="card-footer">
              <b-button
                @click="showDeletePaymentModal(card, 'meshulam')"
                class="btn-del"
                :disabled="isSavingDefault"
              >
                <i></i>
                <span>Delete</span>
              </b-button>
            </div>
          </template>
        </payment-card>
        <b-button
          @click="showAddPaymentModal"
          ref="btnShow"
          v-b-modal.add-payment-method
          class="add-payment-method"
          :disabled="isSavingDefault"
        >
          <i></i>
          <span>Add payment method</span>
        </b-button>
      </div>
    </div>

    <delete-payment-method ref="deletePaymentMethod"></delete-payment-method>
    <add-payment-method ref="addPaymentMethod"></add-payment-method>
  </div>
</template>

<script>
import { mapState } from "vuex";
import axios from "axios";
import urls from "@/constants/urls.js";
import paymentCard from "./subcomponents/payment-card.vue";
import AddPaymentMethod from "./modals/add-payment-method-popup.vue";
import DeletePaymentMethod from "./modals/delete-payment-method-popup.vue";
import { MESHULAM_RETURN_PARAM, takeMeshulamRegistration } from "./meshulam-card.js";

// How long to wait for Meshulam's confirmation after the donor comes back.
const MESHULAM_POLL_INTERVAL_MS = 2000;
const MESHULAM_POLL_ATTEMPTS = 20;
// When the registration can't be checked, reload the cards at these delays.
const MESHULAM_LIST_REFRESH_DELAYS_MS = [3000, 10000, 30000];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default {
  data() {
    return {
      savingCardId: "",
      failedCardId: "",
      paymentError: "",
      // a recurring donation blocks the change: retrying can't help, support must look at it
      needsSupport: false,
      defaultSaved: false,
      meshulamStatus: "",
      meshulamError: "",
      destroyed: false,
    };
  },
  created: function () {
    this.reloadCards();
    this.$store.dispatch("getMeshulamCards");

    const meshulamResult = this.$route.query[MESHULAM_RETURN_PARAM];
    if (meshulamResult) this.finishMeshulamCard(meshulamResult);
  },
  beforeDestroy() {
    this.destroyed = true;
  },
  components: {
    "payment-card": paymentCard,
    "add-payment-method": AddPaymentMethod,
    "delete-payment-method": DeletePaymentMethod,
  },
  methods: {
    showAddPaymentModal() {
      this.$refs.addPaymentMethod.openModal();
    },
    showDeletePaymentModal(card, gateway = "stripe") {
      this.$refs.deletePaymentMethod.openModal(card.id, gateway);
    },
    // finishMeshulamCard checks the card the donor just added on Meshulam's page.
    // Meshulam confirms it to our server separately, so this waits for that.
    async finishMeshulamCard(result) {
      const reference = takeMeshulamRegistration();
      const query = { ...this.$route.query };
      delete query[MESHULAM_RETURN_PARAM];
      this.$router.replace({ query }).catch(() => {});

      if (result !== "done") return;
      if (!reference) return this.refreshMeshulamCardsWithoutReference();

      this.meshulamStatus = "Saving your Meshulam card…";
      const url = urls.donorCardMeshulamRegistration.replace(
        ":reference",
        encodeURIComponent(reference)
      );

      for (let attempt = 0; attempt < MESHULAM_POLL_ATTEMPTS && !this.destroyed; attempt++) {
        try {
          const { data } = await axios.get(url);
          const registration = data.data;

          if (registration.status === "completed") {
            this.meshulamStatus = "Your Meshulam card was added.";
            this.$store.dispatch("getMeshulamCards");
            return;
          }
          if (registration.status === "failed") {
            this.meshulamStatus = "";
            this.meshulamError =
              registration.error || "The card could not be saved. Please try again.";
            return;
          }
        } catch (e) {
          // not this donor's registration: fall back to watching the list; any
          // other error is worth another try
          if (e.response?.status === 404) return this.refreshMeshulamCardsWithoutReference();
        }

        await sleep(MESHULAM_POLL_INTERVAL_MS);
      }

      if (!this.destroyed) {
        this.meshulamStatus =
          "Meshulam has not confirmed your card yet. Refresh this page in a minute to see it.";
      }
    },
    // refreshMeshulamCardsWithoutReference reloads the cards a few times when the
    // registration can't be checked (session storage was unavailable, or the
    // donor returned in another tab).
    async refreshMeshulamCardsWithoutReference() {
      this.meshulamStatus =
        "If you finished Meshulam's page, your card will appear here within a minute.";

      for (const delay of MESHULAM_LIST_REFRESH_DELAYS_MS) {
        await sleep(delay);
        if (this.destroyed) return;
        await this.$store.dispatch("getMeshulamCards");
      }
    },
    async reloadCards() {
      try {
        await this.$store.dispatch("getCards", { throwOnError: true });
        this.paymentError = "";
        this.needsSupport = false;
      } catch {
        this.paymentError = "Unable to load payment methods. Please try again.";
      }
    },
    retryDefaultChange() {
      if (this.failedCardId) return this.setDefaultCard(this.failedCardId);
      return this.reloadCards();
    },
    async setDefaultCard(cardId) {
      if (this.isSavingDefault) return;
      this.savingCardId = cardId;
      this.failedCardId = "";
      this.paymentError = "";
      this.needsSupport = false;
      this.defaultSaved = false;
      let saved = false;
      try {
        await this.$store.dispatch("setDefaultCard", cardId);
        saved = true;
        await this.$store.dispatch("getCards", { throwOnError: true });
        this.defaultSaved = true;
      } catch (error) {
        if (saved) {
          this.paymentError =
            "Your default card was saved, but the list could not be refreshed. Please reload payment methods.";
        } else {
          const apiError = error.response?.data?.errors?.[0];
          this.needsSupport = apiError?.code === "recurring_payment_configuration";
          if (!this.needsSupport) this.failedCardId = cardId;
          this.paymentError =
            apiError?.message ||
            "The change could not be confirmed. Some recurring donations may already use this card. Please retry to finish.";
          try {
            await this.$store.dispatch("getCards", { throwOnError: true });
          } catch {
            this.paymentError += " Payment methods could not be refreshed.";
          }
        }
      } finally {
        this.savingCardId = "";
      }
    },
  },
  computed: {
    ...mapState({
      cards: (state) => state.stripe.cards,
      meshulamCards: (state) => state.stripe.meshulamCards,
    }),
    isSavingDefault() {
      return this.savingCardId !== "";
    },
  },
};
</script>

<style lang="scss">
@import "@/styles/_vars.scss";
.payment-card-container {
  padding: 30px 3% 36px;
  color: #a0a1ac;
  letter-spacing: -0.5px;
  .card-footer {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    .set-default-card {
      margin-right: auto;
      color: #107598;
      white-space: normal;
      text-align: left;
    }
  }
  .scroll-box {
    max-height: 494px;
    overflow: auto;
  }
  .card-desk {
    flex-wrap: wrap;
    display: flex;
  }
  @media (max-width: 767px) {
    .card-desk > .card,
    .card-desk > .add-payment-method {
      min-width: calc(100% - 20px);
      max-width: calc(100% - 20px);
    }
  }
  .add-payment-method {
    background: #fbfdff;
    border: 1px dashed #dee6ef;
    border-radius: 4px;
    max-width: calc(33.33% - 20px);
    min-width: calc(33.33% - 20px);
    margin: 10px 10px 13px;
    display: flex;
    flex-direction: column;
    padding: 60px 20px;
    font-family: "VitoExtended-Light";
    i {
      display: inline-block;
      width: 36px;
      height: 36px;
      border: 1px solid #10759833;
      margin-bottom: 5px;
      border-radius: 18px;
      &:before {
        content: "\002B";
        display: block;
        width: 34px;
        height: 34px;
        font-style: normal;
        font-size: 26px;
        color: #107598;
        line-height: 1.3;
      }
    }
    span {
      display: block;
      text-decoration: underline;
      color: #107598;
    }
    &:focus,
    &:hover {
      outline: none;
      background: #fbfdff !important;
      border: 1px dashed #dee6ef !important;
    }
  }
}
</style>
